import { Link, useLocation, useParams } from 'react-router';
import { ArrowLeft, Building2, FlaskConical, LayoutDashboard, Map, Plus, Rocket, Shield, Users } from 'lucide-react';
import type { WorkspacePublicId } from '@hookmyapp/shared';
import {
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { SidebarFrame } from '@/components/sidebar-frame';
import { SidebarNav, type SidebarNavItem } from '@/components/sidebar-nav';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { WorkspaceSwitcher } from '@/components/workspace-switcher';
import { ChannelList } from '@/components/channel-list';
import { WorkspaceUsageNotice } from '@/components/workspace-usage-notice';
import { useWorkspace } from '@/contexts/workspace-context';
import { useProfile } from '@/contexts/profile-context';
import { useSuperAdminAccess } from '@/hooks/use-super-admin-access';
import { useLaunchpadVisibility } from '@/components/use-launchpad-visibility';
import { workspaceUrl } from '@/lib/workspace-url';
import { orgUrl } from '@/lib/org-url';
import { isValidPublicId } from '@/lib/public-id';

interface AppSidebarProps {
  onConnectClick?: () => void;
}

export function AppSidebar({ onConnectClick }: AppSidebarProps) {
  const { setOpenMobile } = useSidebar();
  const location = useLocation();
  const { activeWorkspace, workspaces } = useWorkspace();
  const { ws: urlWs } = useParams<{ ws: string }>();
  // activeWorkspace lags the URL by one render on deep-link/navigate (see
  // WorkspaceLayout JSDoc). Fall back to the :ws URL param, gated on format so
  // a malformed :ws never reaches workspaceUrl() (which throws).
  const wsId =
    activeWorkspace?.id ??
    (urlWs && isValidPublicId(urlWs, 'ws') ? (urlWs as WorkspacePublicId) : undefined);
  // Resolve the workspace we're chroming — prefer activeWorkspace, fall back to
  // the URL ws (covers the one-render lag) so customer framing never flickers.
  const current = activeWorkspace ?? (wsId ? workspaces.find((w) => w.id === wsId) ?? null : null);
  // A customer is the org's reseller client: drop the switcher for a back link,
  // trim the nav to client-relevant modules, and hide agency-setup chrome.
  const isCustomer = current?.kind === 'customer';
  const orgId =
    current && isValidPublicId(current.organizationPublicId, 'org')
      ? current.organizationPublicId
      : undefined;
  // Back target for a customer: where the user came from (threaded on the row
  // click), else the org Customers list — same as the header breadcrumb org crumb.
  const cameFrom = (location.state as { from?: string } | null)?.from;
  const canConnect = activeWorkspace?.role === 'admin';
  const { profile } = useProfile();
  const { hasAccess: isSuperAdmin } = useSuperAdminAccess(profile?.email ?? null);
  const { show: showLaunchpad, loading: launchpadLoading } = useLaunchpadVisibility();

  // Flat footer nav (everything except the special-cased Launchpad skeleton).
  // Members/Organization/Super Admin intentionally have no isActive highlight.
  const dashboardItem: SidebarNavItem = {
    icon: <LayoutDashboard className="size-4" />,
    label: 'Dashboard',
    to: wsId ? workspaceUrl(wsId) : '#',
    isActive: wsId ? location.pathname === workspaceUrl(wsId) : false,
  };
  const membersItem: SidebarNavItem = {
    icon: <Users className="size-4" />,
    label: 'Members',
    to: wsId ? workspaceUrl(wsId, 'members') : '#',
  };
  // Customer scope hides reseller setup modules. Super Admin remains available
  // in every sidebar scope, gated by the backend access check.
  const navItems: SidebarNavItem[] = [
    ...(isCustomer
      ? [dashboardItem, membersItem]
      : [
          dashboardItem,
          {
            icon: <Rocket className="size-4" />,
            label: 'Quick Start',
            to: wsId ? workspaceUrl(wsId, 'sandbox/quick-start') : '#',
            isActive: wsId ? location.pathname === workspaceUrl(wsId, 'sandbox/quick-start') : false,
          },
          {
            icon: <FlaskConical className="size-4" />,
            label: 'Sandbox',
            to: wsId ? workspaceUrl(wsId, 'sandbox') : '#',
            isActive: wsId ? location.pathname === workspaceUrl(wsId, 'sandbox') : false,
          },
          membersItem,
          {
            icon: <Building2 className="size-4" />,
            label: 'Organization',
            to: orgId ? orgUrl(orgId) : '#',
          },
        ]),
    ...(isSuperAdmin
      ? [{ icon: <Shield className="size-4" />, label: 'Super Admin', to: '/super-admin/organizations' }]
      : []),
  ];

  return (
    <SidebarFrame>
      {/* Fixed h-12 + border-b so the switcher's bottom edge lines up with the
          main content header (also h-12 border-b) — one continuous top line. */}
      <SidebarHeader className="h-12 !p-0 border-b">
        {/* Customer scope swaps the switcher for a back link to the org (same
            target as the breadcrumb org crumb: came-from, else org Customers). */}
        {isCustomer ? (
          <Link
            to={cameFrom ?? (orgId ? orgUrl(orgId, 'customers') : '#')}
            className="flex h-12 items-center gap-2 px-4 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            <span className="truncate group-data-[collapsible=icon]:hidden">Back to Organization</span>
          </Link>
        ) : (
          <WorkspaceSwitcher />
        )}
      </SidebarHeader>

      <SidebarContent>
        {/* pt-6/pb-6 = equal 24px gap above/below the button, matching the main
            content's p-6 spacing. Shown for customers too — a reseller connects
            channels into the client's scope they're operating. */}
        <div className="border-b px-4 pt-6 pb-6 space-y-2 group-data-[collapsible=icon]:px-1.5">
          <Button
            variant="default"
            size="default"
            className="w-full gap-2 group-data-[collapsible=icon]:px-0"
            onClick={() => {
              setOpenMobile(false); // close the mobile drawer before the modal opens
              onConnectClick?.();
            }}
            disabled={!canConnect}
            title="Connect Channel"
          >
            <Plus className="size-4 hidden group-data-[collapsible=icon]:block" />
            <span className="group-data-[collapsible=icon]:hidden">Connect Channel</span>
          </Button>
        </div>
        <ChannelList />
      </SidebarContent>

      {/* px-0 so each footer entry's highlight spans the full sidebar width. */}
      <SidebarFooter className="px-0">
        {/* Org-pool usage banner — self-gates (renders only >=75% or frozen),
            same footer placement as the org sidebar. */}
        <div className="px-1 pb-1">
          <WorkspaceUsageNotice />
        </div>
        <SidebarMenu>
          {/* Launchpad: shown while onboarding is incomplete (fail-visible —
              also shown while loading or on fetch error). Never for customers —
              onboarding is the reseller's own setup, not the client's. */}
          {isCustomer ? null : launchpadLoading ? (
            <SidebarMenuItem>
              <div className="rounded-none px-4 py-2">
                <Skeleton className="h-4 w-20" />
              </div>
            </SidebarMenuItem>
          ) : showLaunchpad ? (
            <SidebarMenuItem>
              <SidebarMenuButton
                render={<Link to={wsId ? workspaceUrl(wsId, 'launchpad') : '#'} />}
                isActive={wsId ? location.pathname === workspaceUrl(wsId, 'launchpad') : false}
                className="rounded-none px-4"
                tooltip="Launchpad"
              >
                <Map className="size-4" />
                <span>Launchpad</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ) : null}

          <SidebarNav items={navItems} />
        </SidebarMenu>
      </SidebarFooter>
    </SidebarFrame>
  );
}
